import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_PROJECTITEMS_LOAD_BY_PROJECT_ID,
    ACTION_PROJECTITEM_LOAD_FAILURE,
    ACTION_PROJECTITEM_LOAD_SUCCESS,

    ACTION_PROJECTITEM_SAVE,
    ACTION_PROJECTITEM_SAVE_SUCCESS,
    ACTION_PROJECTITEM_SAVE_FAILURE,

    ACTION_PROJECTITEM_CREATE,
    ACTION_PROJECTITEM_CREATE_SUCCESS,
    ACTION_PROJECTITEM_CREATE_FAILURE,

    ACTION_PROJECTITEM_CLEAR,
    ACTION_PROJECTITEM_FILTER,

    ProjectItemAction
} from '../actions/projectItems';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from 'src/helpers/formErrorHelper';


export interface IProjectItemState {
    formData: Dtos.ProjectItem | Dtos.ProjectItem[],
        formDataGrouped: Dtos.ProjectItemCategory[];
        formState: Dtos.FormState,
        formProperties: Dtos.FormProperty[],
        validationFailures: Dtos.ResponseError[];
        lookups: Dtos.Lookup[],
        loadState: IRequestState;
        saveState: IRequestState;
        updateState: IRequestState;
        createState: IRequestState;
        formFilter: IFormFilter;
        formFilteredData: Dtos.ProjectItem[];
        formFilteredDataGrouped: Dtos.ProjectItemCategory[];
}

const initialState: IProjectItemState = {
    formData: undefined,
        formDataGrouped: undefined,
        formState: undefined,
        formProperties: undefined,
        validationFailures: undefined,
        lookups: undefined,
        loadState: {
            status: RequestState.None
        } as IRequestState,
        saveState: {
            status: RequestState.None
        } as IRequestState,
        updateState: {
            status: RequestState.None
        } as IRequestState,
        createState: {
            status: RequestState.None
        } as IRequestState,
        formFilter: {
            search: "",
            showInActive: false
        },
        formFilteredData: [],
        formFilteredDataGrouped: undefined,
};


function loading(state: IProjectItemState, _isLoading: boolean): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IProjectItemState,
    formData: Dtos.ProjectItem | Dtos.ProjectItem[],
    formDataGrouped: Dtos.ProjectItemCategory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        formData: {
            $set: formData
        },
        formDataGrouped: {
            $set: formDataGrouped
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IProjectItemState, formFilter: IFormFilter): IProjectItemState {

    const formData: Dtos.ProjectItem[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = formData ? formData.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p?.projectItemTemplate?.name?.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    let formFilteredDataGrouped = [];
    if (state.formDataGrouped) {
        for (var i = 0; i < state.formDataGrouped.length; i++) {
            const filteredCategoryItems =
                state.formDataGrouped[i].projectItems.filter(p => (p.active == true || formFilter.showInActive) &&
                    (formFilter.search == "" ||
                        (p?.projectItemTemplate?.name != undefined && p?.projectItemTemplate?.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1) ||
                        (p?.projectItemTemplate?.description != undefined && p?.projectItemTemplate?.description.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
                    ));
            if (filteredCategoryItems.length > 0) {
                formFilteredDataGrouped.push({
                    ...state.formDataGrouped[i],
                    projectItems: filteredCategoryItems
                });
            }
        }
    }

    const newState: IProjectItemState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formFilteredDataGrouped: {
            $set: formFilteredDataGrouped
        }
    });

    return newState;
}

function loadFailure(state: IProjectItemState, responseStatus: Dtos.ResponseStatus): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IProjectItemState, propertiesToClear?: string[]): IProjectItemState {

    if (!propertiesToClear) {
        const newState: IProjectItemState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IProjectItemState, _isSaving: boolean): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IProjectItemState,
    formData: Dtos.ProjectItem | Dtos.ProjectItem[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IProjectItemState, responseStatus: Dtos.ResponseStatus): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IProjectItemState, _isCreating: boolean): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IProjectItemState,
    formData: Dtos.ProjectItem,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IProjectItemState, responseStatus: Dtos.ResponseStatus): IProjectItemState {
    const newState: IProjectItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const ProjectItemReducer: Reducer<IProjectItemState> = (state: IProjectItemState = initialState, action: ProjectItemAction) => {
    switch (action.type) {
        case ACTION_PROJECTITEMS_LOAD_BY_PROJECT_ID:
            return loading(state, true);
        case ACTION_PROJECTITEM_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formDataGrouped, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_PROJECTITEM_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_PROJECTITEM_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_PROJECTITEM_SAVE:
            return saving(state, true);
        case ACTION_PROJECTITEM_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_PROJECTITEM_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_PROJECTITEM_CREATE:
            return creating(state, true);
        case ACTION_PROJECTITEM_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_PROJECTITEM_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_PROJECTITEM_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default ProjectItemReducer;